<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddAccessLevel from './AddAccessLevel.vue';
import EditAccessLevel from './EditAccessLevel.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedAccessLevel = ref({});

const records = computed(() => getters['accessLevels/getAccessLevels'].value);
const uiFlags = computed(() => getters['accessLevels/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedAccessLevel.value.name}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedAccessLevel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedAccessLevel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteAccessLevel = async id => {
  try {
    await store.dispatch('accessLevels/delete', id);
    useAlert(t('ACCESS_LEVELS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('ACCESS_LEVELS.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedAccessLevel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedAccessLevel.value.id] = true;
  closeDeletePopup();
  deleteAccessLevel(selectedAccessLevel.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('ACCESS_LEVELS.LIST.TABLE_HEADER.NAME'),
    t('ACCESS_LEVELS.LIST.TABLE_HEADER.DESCRIPTION'),
    t('ACCESS_LEVELS.LIST.TABLE_HEADER.CAPABILITIES'),
  ];
});

const formatCapabilities = capabilities => {
  if (!capabilities || !capabilities.length) return '-';
  return (
    capabilities.slice(0, 3).join(', ') + (capabilities.length > 3 ? '...' : '')
  );
};

onBeforeMount(() => {
  store.dispatch('accessLevels/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('ACCESS_LEVELS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('ACCESS_LEVELS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('ACCESS_LEVELS.HEADER')"
        :description="$t('ACCESS_LEVELS.DESCRIPTION')"
        feature-name="accessLevels"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('ACCESS_LEVELS.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="accessLevel in records" :key="accessLevel.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">
                {{ accessLevel.name }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ accessLevel.description || '-' }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="text-sm text-n-slate-11">
                {{ formatCapabilities(accessLevel.capabilities) }}
              </span>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('ACCESS_LEVELS.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[accessLevel.id]"
                  @click="openEditPopup(accessLevel)"
                />
                <Button
                  v-tooltip.top="$t('ACCESS_LEVELS.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[accessLevel.id]"
                  @click="openDeletePopup(accessLevel)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" size="medium" :on-close="hideAddPopup">
      <AddAccessLevel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" size="medium" :on-close="hideEditPopup">
      <EditAccessLevel
        :access-level="selectedAccessLevel"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('ACCESS_LEVELS.DELETE.CONFIRM.TITLE')"
      :message="$t('ACCESS_LEVELS.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('ACCESS_LEVELS.DELETE.CONFIRM.YES')"
      :reject-text="$t('ACCESS_LEVELS.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
